import { svgIconPaths } from '@/assets/svg/icons';
import CustomButton from '@/components/atoms/CustomButton';
import LogoSpinner from '@/components/atoms/LogoSpinner';
import SidebarItemViewTemplate from '@/components/templates/SidebarItemViewTemplate';
import useCurrentOrganizationContext from '@/hooks/super-admin/useCurrentOrganizationContext';
import ClearOrganizationContextConfirmationModal from '@/modules/super-admin/ClearOrganizationContextConfirmationModal';
import SelectOrganizationContextModal from '@/modules/super-admin/SelectOrganizationContextModal';
import colors from '@/theme/colors';
import { HStack, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { createFileRoute } from '@tanstack/react-router';
import { format } from 'date-fns';

const SuperAdminPage = () => {
  const {
    isOpen: isSelectOrgModalOpen,
    onOpen: onSelectOrgModalOpen,
    onClose: onSelectOrgModalClose,
  } = useDisclosure();
  const {
    isOpen: isClearContextModalOpen,
    onOpen: onClearContextModalOpen,
    onClose: onClearContextModalClose,
  } = useDisclosure();

  const { isLoading, data } = useCurrentOrganizationContext();

  const isContextSet = !!data?.organizationId;

  return (
    <SidebarItemViewTemplate title="Organization Access">
      <VStack padding={'40px'} width={'100%'} height={'100%'} alignItems={'flex-start'}>
        <VStack
          background={'white'}
          borderRadius={'20px'}
          boxShadow={'0px 4px 20px rgba(0, 0, 0, 0.1)'}
          padding={'40px'}
          alignItems={'flex-start'}
          maxWidth={'900px'}
          gap={'30px'}
        >
          <HStack justifyContent={'space-between'} width={'100%'}>
            <Text variant={'urbanistSemiBold'} color={'text.darkBlue'} fontSize={'26px'}>
              Access Organization as Super Admin
            </Text>
            <HStack alignItems={'center'} gap={'20px'}>
              {isLoading && <LogoSpinner width="48px" />}
              {!isContextSet && (
                <CustomButton
                  onClick={onSelectOrgModalOpen}
                  backgroundColor="white"
                  hoverBackgroundColor={'primary.100'}
                  label="Edit"
                  labelColor="text.darkBlue"
                  style={{
                    padding: 0,
                    borderColor: colors.text.darkBlue,
                    borderWidth: '2px',
                  }}
                  disabled={isContextSet}
                  width={'110px'}
                  height={'55px'}
                  iconBeforeLabelSvgPath={svgIconPaths.pen}
                  iconBeforeLabelSvgSize={16}
                />
              )}
            </HStack>
          </HStack>
          <Text>
            Select an organization you want to view in the client application. Once selected, you’ll be able to access
            the client app in read-only mode as if you were part of that organization. You will not be able to modify
            any data, and your account will not appear in the organization’s user list.
          </Text>
          {isContextSet && (
            <HStack width={'100%'} alignItems={'center'} justifyContent={'space-between'}>
              <VStack alignItems={'flex-start'} gap={'20px'}>
                <Text variant={'urbanistSemiBold'} color={'text.mediumGray'} fontSize={'12px'}>
                  Current Organization
                </Text>
                <Text variant={'urbanistSemiBold'} color={'text.darkBlue'} fontSize={'16px'}>
                  {data?.organizationName || 'No organization selected'}
                </Text>
              </VStack>
              <VStack alignItems={'flex-start'} gap={'20px'}>
                <Text variant={'urbanistSemiBold'} color={'text.mediumGray'} fontSize={'12px'}>
                  ID
                </Text>
                <Text variant={'urbanistSemiBold'} color={'text.darkBlue'} fontSize={'16px'}>
                  {data?.organizationId || 'No organization selected'}
                </Text>
              </VStack>
              <VStack alignItems={'flex-start'} gap={'20px'}>
                <Text variant={'urbanistSemiBold'} color={'text.mediumGray'} fontSize={'12px'}>
                  Expires At
                </Text>
                <Text variant={'urbanistSemiBold'} color={'text.darkBlue'} fontSize={'16px'}>
                  {data?.expiresAt ? format(new Date(data.expiresAt), 'dd. MMM h:mm a') : 'No organization selected'}
                </Text>
              </VStack>
              <CustomButton
                label="Remove Access"
                backgroundColor="extra.red"
                onClick={() => onClearContextModalOpen()}
              />
            </HStack>
          )}
        </VStack>
      </VStack>
      <SelectOrganizationContextModal isOpen={isSelectOrgModalOpen} onClose={onSelectOrgModalClose} />
      <ClearOrganizationContextConfirmationModal isOpen={isClearContextModalOpen} onClose={onClearContextModalClose} />
    </SidebarItemViewTemplate>
  );
};

export const Route = createFileRoute('/_authenticated/_admin/super-admin/organization-access')({
  component: SuperAdminPage,
});
